import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { X } from 'lucide-react';
import { useToast } from '@/context/ToastContext';
import { useAuth } from '@/context/AuthContext';
import { CountryCitySelector } from '@/components/CountryCitySelector';
import ModernAnnonceCard from '@/components/ModernAnnonceCard';
import { StepBar, StepCard, StepNav, Field, ChoicePills, ChoiceCards, CurrencyPicker, DraftButton, RecapLinks } from '@/components/publish/PublishKit';
import { ccySym } from '@/utils/currencyUtils';
import { calcShipmentMinPrice, ZERO_DEC_CCY, STATIC_RATES_EUR } from '@/utils/tripPriceUtils';
import { CONSTRAINT_META, CARRIER_PREF_META, plainLabel } from '@/utils/listingParsers';
import {
  SHIPMENT_STEPS, FLEX_OPTIONS, URGENCY_OPTIONS, shipmentStepErrors, firstInvalidStep, toggleChoice, shipmentPayload,
  normalizeFlex, readDraft, saveDraft, clearDraft,
} from '@/utils/publishForm';
import { canDo } from '@/utils/verifications';
import VerificationGate from '@/components/verification/VerificationGate';
import { isEntreprise, isEntreprisePending } from '@/utils/entreprise';
import { compressImage } from '@/utils/photos';
import { BACKEND_URL } from '@/config';

const API = `${BACKEND_URL}/api`;
const DRAFT_KEY = 'c2s_draft_shipment';

// Valeurs enregistrées telles quelles (annonces existantes) : ne pas les renommer
const CATEGORIES = [
  'Vêtements / Textile', 'Électronique', 'Documents administratifs', 'Cosmétiques / Hygiène',
  'Médicaments / Santé', 'Alimentation', 'Cadeaux personnels', 'Autre',
];
// Les six plus courantes d'abord ; « Assurance incluse » n'est plus proposée (la plateforme n'en vend pas, 2026-11-03)
const CONSTRAINT_ORDER = ['fragile', 'upright', 'no_stack', 'valuable', 'signature', 'perishable',
  'very_fragile', 'temp', 'cold_chain', 'electronic', 'urgent', 'insurance', 'bulky', 'careful', 'other'];
const PREF_ORDER = ['verified', 'no_stop', 'fragile_ok', 'weekend', 'french', 'professional',
  'particular', 'fast', 'direct', 'evening', 'ac_vehicle', 'bulky_ok', 'english', 'experienced'];
const CONSTRAINTS = CONSTRAINT_ORDER.map((id) => ({ id, label: plainLabel(CONSTRAINT_META[id].label) }));
const PREFS = PREF_ORDER.map((id) => ({ id, label: plainLabel(CARRIER_PREF_META[id].label) }));

const EMPTY = {
  title: '', origCountry: '', origCity: '', destCountry: '', destCity: '', date: '', flex: 'fixe', urgency: 'standard',
  weightKg: '', dimL: '', dimW: '', dimH: '', categories: [], categoryCustom: '',
  budgetLibre: false, priceOffered: '', currencies: ['EUR'], primaryCurrency: 'EUR', counterOffers: true,
  description: '', photos: [], constraints: [], constraintCustom: '', carrierPreferences: [], message: '',
};

/** Annonce enregistrée (ou dupliquée) → état du formulaire. */
function fromShipment(s, { keepDate }) {
  const [dimL = '', dimW = '', dimH = ''] = (s.dimensions || '').replace(' cm', '').split('×').map((x) => x.trim().replace('?', ''));
  return {
    ...EMPTY,
    title: s.title || '', origCountry: s.departure_country || '', origCity: s.departure_city || '',
    destCountry: s.arrival_country || '', destCity: s.arrival_city || '',
    date: keepDate ? (s.departure_date?.slice(0, 10) || '') : '',
    flex: normalizeFlex(s.flex_departure), urgency: s.urgency || 'standard',
    weightKg: s.weight_kg != null ? String(s.weight_kg) : '', dimL, dimW, dimH,
    categories: Array.isArray(s.categories) ? s.categories : [], categoryCustom: s.category_custom || '',
    budgetLibre: !s.price_offered, priceOffered: s.price_offered ? String(s.price_offered) : '',
    currencies: s.currencies?.length ? s.currencies : ['EUR'], primaryCurrency: s.primary_currency || 'EUR',
    counterOffers: s.counter_offers !== false, description: s.description || '',
    constraints: Array.isArray(s.constraints) ? s.constraints : [], constraintCustom: s.constraint_custom || '',
    carrierPreferences: Array.isArray(s.carrier_preferences) ? s.carrier_preferences : [], message: s.message_to_carriers || '',
  };
}

const fmt = (n, ccy) => Number(n).toLocaleString('fr-FR', { maximumFractionDigits: ZERO_DEC_CCY.has(ccy) ? 0 : 2 });

/**
 * Publier un colis — parcours en quatre étapes (direction C, planche C-Publier-colis, 2026-11-03) : le trajet, le
 * colis, le prix, les détails (facultatifs), puis le récapitulatif sous forme de carte d'annonce.
 * Règles : `utils/publishForm.js` ; modification et duplication passent par les mêmes étapes.
 */
export default function ShipmentForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user } = useAuth();
  const { id } = useParams();
  const isEdit = !!id;
  const isDuplicate = new URLSearchParams(location.search).get('duplicate') === '1';
  const isPro = isEntreprise(user);
  const proPending = isEntreprisePending(user);
  const maxWeight = isPro ? 1500 : 250;

  // Brouillon : seulement pour une nouvelle annonce, et seulement s'il contient déjà quelque chose
  const [draft] = useState(() => {
    const d = !isEdit && !isDuplicate ? readDraft(DRAFT_KEY) : null;
    return d && (d.origCountry || d.destCountry || d.date || d.weightKg || d.title) ? { ...EMPTY, ...d } : null;
  });
  const [f, setF] = useState(draft || EMPTY);
  const [draftNote, setDraftNote] = useState(!!draft);
  const [step, setStep] = useState('route');
  const [tried, setTried] = useState({});
  const [fetching, setFetching] = useState(isEdit);
  const [loading, setLoading] = useState(false);
  const [ai, setAi] = useState({ desc: false, msg: false });
  const [eurRates, setEurRates] = useState(STATIC_RATES_EUR);
  const top = useRef(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    if (isEdit) {
      axios.get(`${API}/shipments/${id}`)
        // Annonce expirée : la republier demande une nouvelle date (le serveur refuse l'ancienne)
        .then((r) => setF(fromShipment(r.data, { keepDate: r.data.status !== 'EXPIRED' })))
        .catch(() => toast.error('Annonce introuvable'))
        .finally(() => setFetching(false));
    } else if (isDuplicate) {
      try {
        const stored = sessionStorage.getItem('c2s_shipment_duplicate');
        if (stored) {
          const d = JSON.parse(stored);
          setF({ ...fromShipment(d, { keepDate: false }), title: d.title ? `Copie — ${d.title}`.slice(0, 50) : '' });
          sessionStorage.removeItem('c2s_shipment_duplicate');
          toast.success('Formulaire prérempli — adaptez les informations avant de publier.');
        }
      } catch { /* stockage indisponible */ }
    }
  }, [id, isEdit, isDuplicate, toast]);

  useEffect(() => { if (!isEdit && !isDuplicate) saveDraft(DRAFT_KEY, f); }, [f, isEdit, isDuplicate]);

  useEffect(() => {
    axios.get(`${API}/currencies/rates`)
      .then((r) => { if (r.data?.rates) setEurRates((prev) => ({ ...prev, ...r.data.rates })); })
      .catch(() => {});
  }, []);

  const ccy = f.primaryCurrency;
  const sym = ccySym(ccy);
  // Budget minimum = 1 € converti dans la devise principale, au taux du jour (le serveur contrôle aussi)
  const minPrice = useMemo(() => calcShipmentMinPrice(ccy, eurRates), [eurRates, ccy]);
  const minPriceText = `${fmt(minPrice, ccy)} ${sym}${ccy !== 'EUR' ? ' (≈ 1 €)' : ''}`;
  const ctx = { maxWeight, minPrice, minPriceText };
  const errorsOf = (k) => shipmentStepErrors(k, f, ctx);
  const err = tried[step] ? errorsOf(step) : {};

  const go = (k) => { setStep(k); top.current?.scrollIntoView?.({ block: 'start' }); };
  const idx = SHIPMENT_STEPS.findIndex((s) => s.key === step);
  const next = () => {
    const e = errorsOf(step);
    if (Object.keys(e).length) {
      setTried((t) => ({ ...t, [step]: true }));
      setTimeout(() => document.querySelector('.pf-card [aria-invalid="true"], .pf-card .pf-err')?.focus?.(), 0);
      return;
    }
    go(SHIPMENT_STEPS[idx + 1].key);
  };
  const back = idx > 0 ? () => go(SHIPMENT_STEPS[idx - 1].key) : null;

  const generate = async (kind) => {
    setAi((a) => ({ ...a, [kind]: true }));
    const route = { origin: f.origCity || f.origCountry, destination: f.destCity || f.destCountry };
    try {
      if (kind === 'desc') {
        const r = await axios.post(`${API}/ai/shipment-description`, { ...route, categories: f.categories, weight_kg: f.weightKg ? Number(f.weightKg) : undefined });
        set('description', (r.data.description ?? '').slice(0, 120));
      } else {
        const labels = (ids, meta) => ids.map((x) => plainLabel(meta[x]?.label || x));
        const r = await axios.post(`${API}/ai/shipment-message`, {
          ...route, constraints: labels(f.constraints, CONSTRAINT_META), carrier_preferences: labels(f.carrierPreferences, CARRIER_PREF_META), primary_currency: ccy,
        });
        set('message', (r.data.message ?? '').slice(0, 150));
      }
    } catch {
      toast.error('La rédaction automatique ne répond pas : écrivez quelques mots vous-même.');
    } finally {
      setAi((a) => ({ ...a, [kind]: false }));
    }
  };

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - f.photos.length);
    for (const file of files) {
      // Compressée avant l'envoi (≈ 1 600 px, JPEG) : quelques centaines de Ko au lieu de plusieurs Mo (T11)
      try {
        const dataUrl = await compressImage(file);
        setF((x) => ({ ...x, photos: [...x.photos, { name: file.name, dataUrl }] }));
      } catch (error) {
        toast.error(`${file.name} : ${error.message}`);
      }
    }
    e.target.value = '';
  };

  const submit = async () => {
    const bad = firstInvalidStep(SHIPMENT_STEPS, errorsOf);
    if (bad) { setTried((t) => ({ ...t, [bad]: true })); go(bad); return; }
    setLoading(true);
    try {
      const payload = shipmentPayload(f);
      if (isEdit) {
        await axios.patch(`${API}/shipments/${id}`, payload);
        toast.success('Annonce mise à jour — en attente de validation.');
        navigate(`/app/shipments/${id}`);
      } else {
        const res = await axios.post(`${API}/shipments`, payload);
        clearDraft(DRAFT_KEY);
        toast.success('Annonce publiée — en attente de validation.');
        navigate(`/app/shipments/${res.data.id}`);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.msg || String(d)).join(' ; ') : (detail || 'Erreur lors de la publication'));
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <div className="pf-page"><p className="pf-sub">Chargement…</p></div>;

  const title = isEdit ? "Modifier l'annonce" : isDuplicate ? 'Dupliquer une annonce' : 'Publier un colis';
  if (!isEdit && user && !canDo(user, 'publish')) {
    return (
      <div className="pf-page">
        <h1 className="pf-h1">{title}</h1>
        <VerificationGate user={user} action="publish" goal="publier un colis" />
      </div>
    );
  }

  const stepNo = (k) => `Étape ${SHIPMENT_STEPS.findIndex((s) => s.key === k) + 1} sur 4`;
  const preview = {
    id: 'apercu', type: 'colis', from: f.origCity || '…', to: f.destCity || '…',
    price: f.budgetLibre ? null : Number(f.priceOffered) || null, unit: sym, weight: Number(f.weightKg) || null,
    hot: false, daysNum: null,
    user: {
      name: `${user?.first_name || ''} ${(user?.last_name || '').slice(0, 1)}.`.trim(),
      score: user?.avg_rating ? user.avg_rating.toFixed(1).replace('.', ',') : 'Nouveau', trips: user?.rating_count || 0,
      verified: !!user?.is_verified, pro: isPro, proPending,
    },
    raw: { departure_date: f.date, urgency: f.urgency, flex_departure: f.flex, author: { first_name: user?.first_name, last_name: user?.last_name } },
  };

  return (
    <div className="pf-page" ref={top}>
      <div>
        <button type="button" className="pf-back" onClick={() => navigate('/app/shipments')} data-testid="btn-cancel">← Mes colis</button>
        <h1 className="pf-h1">{title}</h1>
        <p className="pf-sub">
          Quatre étapes, deux minutes. Rien n'est débité à la publication.
          {isDuplicate && ' Formulaire prérempli : adaptez-le avant de publier.'}
        </p>
      </div>
      {draftNote && step === 'route' && (
        <p className="pf-note" data-testid="draft-restored">
          Nous avons gardé ce que vous aviez commencé (sans les photos).{' '}
          <button type="button" className="pf-link" onClick={() => { clearDraft(DRAFT_KEY); setF(EMPTY); setTried({}); setDraftNote(false); }} data-testid="draft-clear">Tout effacer</button>
        </p>
      )}

      <StepBar steps={SHIPMENT_STEPS} current={step} onGo={go} />

      {step === 'route' && (
        <StepCard eyebrow={stepNo('route')} title="D'où part votre colis, et où va-t-il ?" testid="step-card-route">
          <div className="pf-places">
            <CountryCitySelector dirLabel="Départ" country={f.origCountry} onCountryChange={(v) => set('origCountry', v)}
              city={f.origCity} onCityChange={(v) => set('origCity', v)} errCountry={err.origCountry} errCity={err.origCity} />
            <CountryCitySelector dirLabel="Arrivée" country={f.destCountry} onCountryChange={(v) => set('destCountry', v)}
              city={f.destCity} onCityChange={(v) => set('destCity', v)} errCountry={err.destCountry} errCity={err.destCity} />
          </div>
          <div className="pf-field-narrow">
            <Field id="f-date" label="Il doit partir avant le" error={err.date} render={(p) => (
              <input {...p} type="date" value={f.date} min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => set('date', e.target.value)} data-testid="input-date" />
            )} />
          </div>
          <ChoicePills legend="Cette date est-elle souple ?" options={FLEX_OPTIONS} value={f.flex} onChange={(v) => set('flex', v)}
            testid={(o) => `flex-${o.id}`} />
          <ChoicePills legend="Est-ce pressé ?" options={URGENCY_OPTIONS} value={f.urgency} onChange={(v) => set('urgency', v)}
            hint="Affiché sur votre annonce, pour que les transporteurs le sachent." testid={(o) => `urgency-${o.id}`} />
          <StepNav onNext={next} />
        </StepCard>
      )}

      {step === 'parcel' && (
        <StepCard eyebrow={stepNo('parcel')} title="Votre colis" testid="step-card-parcel">
          <div className="pf-grid">
            <Field id="f-weight" label="Combien pèse-t-il ?" suffix="kg" error={err.weightKg}
              hint={`Entre 0,1 et ${maxWeight.toLocaleString('fr-FR')} kg${isPro ? ' (Entreprise)' : proPending ? ' (1 500 kg une fois votre entreprise validée)' : ''}.`}
              render={(p) => (
                <input {...p} className="pf-input" type="number" inputMode="decimal" step="0.1" min="0.1" max={maxWeight}
                  value={f.weightKg} onChange={(e) => set('weightKg', e.target.value)} data-testid="input-weight" />
              )} />
            <fieldset className="pf-fieldset">
              <legend className="pf-label">Dimensions<span className="pf-optional"> (facultatif, en cm)</span></legend>
              <div className="pf-dims">
                {[['dimL', 'Longueur', 'L'], ['dimW', 'Largeur', 'l'], ['dimH', 'Hauteur', 'H']].map(([k, name, ph]) => (
                  <input key={k} className="pf-input" type="number" min="1" placeholder={ph} aria-label={`${name} (cm)`}
                    value={f[k]} onChange={(e) => set(k, e.target.value)} />
                ))}
              </div>
            </fieldset>
          </div>
          <ChoicePills legend="Que contient-il ?" multi max={5} optional options={CATEGORIES.map((c) => ({ id: c, label: c }))}
            value={f.categories} onChange={(v) => set('categories', toggleChoice(f.categories, v))} testid={(o) => `category-${o.id}`} />
          {f.categories.includes('Autre') && (
            <Field id="f-cat-custom" label="Précisez la catégorie" error={err.categoryCustom} render={(p) => (
              <input {...p} type="text" maxLength={60} placeholder="Jouets, pièces détachées…" value={f.categoryCustom}
                onChange={(e) => set('categoryCustom', e.target.value)} />
            )} />
          )}
          <StepNav onBack={back} onNext={next} />
        </StepCard>
      )}

      {step === 'price' && (
        <StepCard eyebrow={stepNo('price')} title="Votre budget" testid="step-card-price">
          <CurrencyPicker currencies={f.currencies} primary={ccy} error={err.currencies}
            onChange={(list, primary) => setF((x) => ({ ...x, currencies: list, primaryCurrency: primary }))} />
          <ChoiceCards legend="Comment fixer le prix ?" name="pf-budget" value={f.budgetLibre ? 'libre' : 'budget'}
            onChange={(v) => setF((x) => ({ ...x, budgetLibre: v === 'libre' }))} testid={(o) => `budget-${o.id}`}
            options={[
              { id: 'budget', title: "J'ai un budget", text: 'Les transporteurs le voient et peuvent l’accepter.' },
              { id: 'libre', title: 'Budget libre', text: 'Les transporteurs vous proposent leur prix.' },
            ]} />
          {!f.budgetLibre && (
            <div className="pf-field-narrow">
              <Field id="f-budget" label="Votre budget" suffix={sym} hint={`${minPriceText} au moins.`} error={err.priceOffered}
                render={(p) => (
                  <input {...p} className="pf-input" type="number" inputMode="decimal" step="any" min={minPrice}
                    value={f.priceOffered} onChange={(e) => set('priceOffered', e.target.value)} data-testid="input-price" />
                )} />
            </div>
          )}
          <ChoicePills legend="Les transporteurs peuvent-ils négocier ?" value={f.counterOffers ? 'open' : 'firm'}
            onChange={(v) => set('counterOffers', v === 'open')} testid={(o) => `negoc-${o.id}`}
            options={[{ id: 'open', label: 'Oui, ouvert aux contre-offres' }, { id: 'firm', label: 'Non, budget ferme' }]} />
          <p className="pf-note pf-note-money" data-testid="publish-no-charge">
            Rien n'est débité à la publication : vous payez quand une proposition est acceptée, ou en réservant directement un trajet.
          </p>
          <StepNav onBack={back} onNext={next} />
        </StepCard>
      )}

      {step === 'details' && (
        <StepCard eyebrow={`${stepNo('details')} · facultative`} title="Les détails qui rassurent" testid="step-card-details">
          <Field id="f-title" label="Titre de l'annonce" hint={`Laissé vide : « Colis ${f.origCity || 'départ'} → ${f.destCity || 'arrivée'} ».`}
            render={(p) => (
              <input {...p} type="text" maxLength={50} value={f.title} onChange={(e) => set('title', e.target.value.slice(0, 50))}
                placeholder="Documents importants et vêtements" data-testid="input-title" />
            )} />
          <div className="pf-field">
            <div className="pf-row-between">
              <label className="pf-label" htmlFor="f-desc">Description</label>
              <DraftButton onClick={() => generate('desc')} busy={ai.desc} />
            </div>
            <textarea id="f-desc" className="pf-textarea" rows={3} maxLength={120} value={f.description}
              placeholder="Contexte de l'envoi, ce qui aidera le transporteur…" onChange={(e) => set('description', e.target.value.slice(0, 120))}
              data-testid="input-description" />
            <p className="pf-count">{f.description.length}/120</p>
          </div>
          <div className="pf-field">
            <span className="pf-label" id="f-photos-label">Photos<span className="pf-optional"> (3 au plus, réduites automatiquement)</span></span>
            <div className="pf-photos" aria-labelledby="f-photos-label">
              {f.photos.map((p, i) => (
                <div key={p.name + i} className="pf-photo">
                  <img src={p.dataUrl} alt={`Colis, vue ${i + 1}`} />
                  <button type="button" className="pf-photo-del" aria-label={`Retirer la photo ${i + 1}`}
                    onClick={() => setF((x) => ({ ...x, photos: x.photos.filter((_, k) => k !== i) }))}>
                    <X size={14} aria-hidden="true" />
                  </button>
                </div>
              ))}
              {f.photos.length < 3 && (
                <label className="pf-photo-add">
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={addPhotos} className="sr-only" data-testid="input-photos" />
                  <span aria-hidden="true">+</span> Ajouter
                </label>
              )}
            </div>
          </div>
          <ChoicePills legend="Précautions" multi max={5} options={CONSTRAINTS} value={f.constraints} visible={6} moreLabel="précautions"
            onChange={(v) => set('constraints', toggleChoice(f.constraints, v))} testid={(o) => `constraint-${o.id}`} />
          {f.constraints.includes('other') && (
            <Field id="f-const-custom" label="Précisez la précaution" error={err.constraintCustom} render={(p) => (
              <input {...p} type="text" maxLength={80} value={f.constraintCustom} placeholder="Ne pas exposer au soleil…"
                onChange={(e) => set('constraintCustom', e.target.value)} />
            )} />
          )}
          <ChoicePills legend="Le transporteur idéal" multi max={5} options={PREFS} value={f.carrierPreferences} visible={6} moreLabel="préférences"
            onChange={(v) => set('carrierPreferences', toggleChoice(f.carrierPreferences, v))} testid={(o) => `pref-${o.id}`} />
          <div className="pf-field">
            <div className="pf-row-between">
              <label className="pf-label" htmlFor="f-message">Un mot aux transporteurs</label>
              <DraftButton onClick={() => generate('msg')} busy={ai.msg} />
            </div>
            <textarea id="f-message" className="pf-textarea" rows={2} maxLength={150} value={f.message}
              placeholder="Remise, contact à l'arrivée…" onChange={(e) => set('message', e.target.value.slice(0, 150))} data-testid="input-message" />
            <p className="pf-count">{f.message.length}/150</p>
          </div>
          <StepNav onBack={back} onNext={next} nextLabel="Voir le récapitulatif" onSkip={() => go('recap')} />
        </StepCard>
      )}

      {step === 'recap' && (
        <StepCard eyebrow="Récapitulatif" title="Voici ce que verront les transporteurs" testid="step-card-recap">
          <div className="pf-recap-card"><ModernAnnonceCard a={preview} preview /></div>
          <RecapLinks steps={SHIPMENT_STEPS} onGo={go} />
          <p className="pf-hint">L'équipe Colis2Ship vérifie chaque annonce avant de la rendre visible.</p>
          <StepNav onBack={back} onNext={submit} busy={loading} nextTestid="btn-submit"
            nextLabel={loading ? 'Publication…' : isEdit ? "Mettre à jour l'annonce" : 'Publier le colis'} />
        </StepCard>
      )}
    </div>
  );
}
